import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import { listApplicationReviews } from '../../api/applicationForm';
import { PageHeader } from '../../components/PageHeader';
import { APPLICATION_FORM_STATUS_LABEL } from '../portal/applicationFormStatus';
import { NumberLabel } from '../../components/NumberLabel';

/** Applications students submitted in the Student Portal and waiting for
 * review (2026-11-06) — a Counselor sees their own students only. Each opens
 * on the lead's Personal Appointment, where it is reviewed. */
export const ApplicationReviewsPage = () => {
  const query = useQuery({ queryKey: ['application-reviews'], queryFn: listApplicationReviews });
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        icon={AssignmentTurnedInIcon}
        color="#5B2DA3"
        title="Application Reviews"
        subtitle="University Application Forms submitted by students in the Student Portal, waiting for your review."
      />
      {query.isLoading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress />
        </Stack>
      )}
      {query.isError && <Alert severity="error">The review queue could not be loaded.</Alert>}
      {query.data && (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Application reviews">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Application #</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Submitted</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Counsellor</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Branch</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {query.data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No applications are waiting for review.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {query.data.map((r) => (
                <TableRow key={r.studentId} hover>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>
                    <NumberLabel value={r.applicationNumber} kind="application" />
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color="warning" label={`${APPLICATION_FORM_STATUS_LABEL[r.status]}${r.version > 1 ? ` (v${r.version})` : ''}`} />
                  </TableCell>
                  <TableCell>{r.submittedAt ? new Date(r.submittedAt).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</TableCell>
                  <TableCell>{r.counsellor ?? '—'}</TableCell>
                  <TableCell>{r.branch ?? '—'}</TableCell>
                  <TableCell align="right">
                    {r.leadId && (
                      <Button size="small" variant="outlined" component={RouterLink} to={`/leads/${r.leadId}`}>
                        Review
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};
